import { useMemo } from 'react';
import type { BillableLine } from '@/api/vorderingen';
import { closeOutWholePercent, floorWholePercent, wholePercent } from './vorderingHelpers';

const fmtEur = (n: number): string =>
  new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(n || 0);

/** A line has billing headroom left when amount − billed is more than a rounding cent. */
export const hasHeadroom = (l: BillableLine): boolean => l.amount - l.billed > 0.005;

/** Per-line entry state — a checked line's own % (raw string, empty = "use the
 *  automatic suggestion") and an optional free-text description. */
export interface LineEntry {
  percentage: string;
  description: string;
}

export const emptyLineEntry: LineEntry = { percentage: '', description: '' };

/**
 * What a CHECKED line bills: the whole percent and the euro that follows from it.
 * The single rule behind the picker's preview, the "alles gevorderd" check and the
 * submit payload — so the einde-werken question can never disagree with the number
 * the PL just read on screen. The server stays authoritative for the definitive amount.
 */
export function lineBillingPreview(
  l: BillableLine,
  entry: LineEntry | undefined,
  autoPercentage: number,
  eindeWerken?: boolean,
): { requestedPct: number; pct: number; amount: number } {
  const remaining = Math.max(0, l.amount - l.billed);
  const raw = (entry ?? emptyLineEntry).percentage.trim();
  const manualNum = raw === '' ? null : Number(raw);
  // WHOLE percent everywhere — the API rounds at its own boundary and Odoo can
  // only invoice whole percent, so previewing anything finer would show a euro
  // figure the client is never billed.
  const requestedPct = manualNum !== null && !Number.isNaN(manualNum)
    ? wholePercent(Math.min(100, Math.max(0, manualNum)))
    : wholePercent(autoPercentage);
  // The headroom ceiling FLOORS (never bill over the line); einde werken closes
  // the line out at the whole percent nearest its remaining.
  const ceilingPct = l.amount > 0 ? floorWholePercent((remaining / l.amount) * 100) : 0;
  const pct = eindeWerken
    ? closeOutWholePercent(remaining, l.amount)
    : Math.min(requestedPct, ceilingPct);
  return { requestedPct, pct, amount: Math.round((pct / 100) * l.amount * 100) / 100 };
}

/**
 * True when this vordering leaves NO headroom on ANY billable line: every open
 * line is checked and billed down to its remaining. That is the shape of a
 * close-out, so the host asks whether it should be «einde werken» before creating
 * it — a PL who bills the last euro but forgets the toggle leaves the project
 * without an einde-werken marker and without the einde-werken fase flip.
 */
export function closesOutAllLines(
  lines: BillableLine[],
  entries: Map<number, LineEntry>,
  autoPercentage: number,
): boolean {
  const open = lines.filter(hasHeadroom);
  if (open.length === 0) return false;
  return open.every((l) => {
    if (!entries.has(l.id)) return false;
    const { amount } = lineBillingPreview(l, entries.get(l.id), autoPercentage, false);
    // Same one-cent tolerance as hasHeadroom, applied to what is left AFTER this
    // vordering. A whole-percent residue (a line that cannot close on a whole
    // percent) is real headroom — it is not a close-out and must not ask.
    return l.amount - l.billed - amount <= 0.005;
  });
}

/**
 * Default line selection.
 * - Einde werken: bills the WHOLE contract closeout — every line with headroom
 *   is pre-checked (each with an empty/auto entry; einde ignores the per-line %
 *   and bills the full remaining anyway), so a PL can't accidentally close out
 *   the project while only a subset of lines got billed.
 * - Otherwise (ADR: forces an explicit choice on multi-systeem orders): exactly
 *   one line with headroom → pre-check it; two or more → pre-check none.
 */
export function defaultLineEntries(lines: BillableLine[], eindeWerken?: boolean): Map<number, LineEntry> {
  const open = lines.filter(hasHeadroom);
  const entries = new Map<number, LineEntry>();
  if (eindeWerken) {
    for (const l of open) entries.set(l.id, { ...emptyLineEntry });
    return entries;
  }
  if (open.length === 1) entries.set(open[0].id, { ...emptyLineEntry });
  return entries;
}

export interface LineBillingPickerProps {
  lines: BillableLine[];
  /** Checked lines and their own percentage/description — the host owns this map. */
  entries: Map<number, LineEntry>;
  onToggleLine: (id: number) => void;
  onEntryChange: (id: number, patch: Partial<LineEntry>) => void;
  /** The existing m²-derived suggestion (0..100) — the default shown per line
   *  (as a placeholder) and used for the preview when a line's own % is empty. */
  autoPercentage: number;
  /** The m²-basis warning that explains where autoPercentage came from — rendered
   *  right above the lines it applies to, where the € figure is decided. */
  m2Notice?: React.ReactNode;
  /** Einde werken bills each selected line's FULL remaining headroom (the server
   *  ignores every per-line %). The per-line % input is hidden in that case and
   *  the preview shows the full payoff — mirroring what the server will bill. */
  eindeWerken?: boolean;
  /** Render the "Selecteer minstens één offertelijn." block — the host decides
   *  whether a line is required (bills against contract) and none is checked. */
  showRequiredError?: boolean;
  /** Render "Vul een percentage in." under a checked (non-einde) line whose own
   *  % is empty and there are no collectors to fall back on (an empty % there
   *  bills €0) — the host decides when this warning is live (e.g. after touch). */
  showPercentageWarning?: boolean;
}

/**
 * "Te vorderen offertelijnen" picker — used by the Dashboard inline create form.
 * Lets the PL pick which sale-order line(s) a vordering bills against; each
 * CHECKED line carries its own optional percentage override and description. A
 * live € preview is shown per line + a bold total. The server remains
 * authoritative for the definitive amount — this is a display estimate only.
 */
export function LineBillingPicker({
  lines,
  entries,
  onToggleLine,
  onEntryChange,
  autoPercentage,
  m2Notice,
  eindeWerken,
  showRequiredError,
  showPercentageWarning,
}: LineBillingPickerProps) {
  const openCount = useMemo(() => lines.filter(hasHeadroom).length, [lines]);

  const preview = useMemo(
    () => lines
      .filter((l) => entries.has(l.id))
      .map((l) => {
        const entry = entries.get(l.id) ?? emptyLineEntry;
        const { requestedPct, pct, amount } = lineBillingPreview(l, entry, autoPercentage, eindeWerken);
        const emptyPct = !eindeWerken && entry.percentage.trim() === '' && wholePercent(autoPercentage) <= 0;
        // Asked for more than the line still has open, so the ceiling kicked in and
        // the row below bills the remaining instead. Without saying so the preview
        // just silently relabels "50%" as "33%", which reads as a miscalculation.
        // Einde werken is excluded: billing the full remaining IS its job, not a cap.
        const capped = !eindeWerken && requestedPct > pct;
        const label = [l.name, l.detail].filter(Boolean).join(' · ');
        return {
          id: l.id, name: label, pct, lineAmount: l.amount, amount, emptyPct,
          capped, requestedPct,
        };
      }),
    [lines, entries, autoPercentage, eindeWerken],
  );
  const total = preview.reduce((sum, p) => sum + p.amount, 0);

  // The suggestion rendered into an untouched % field. Blank when there is no
  // suggestion yet (no collectors picked → 0%): showing "0.0" there would read as
  // a deliberate €0 and hide the "Vul een percentage in" warning behind a value
  // that looks filled in.
  const autoPct = wholePercent(autoPercentage) > 0 ? String(wholePercent(autoPercentage)) : '';

  // Group by sale order. A project's lines can span the base contract and one or
  // more meerprijs SOs; without the S-number header, two identically-labelled
  // "Uw nettoprijs" lines from different orders are indistinguishable. Groups keep
  // the server's line order, and a single-SO project renders no header at all —
  // the common case stays exactly as it looked before.
  const groups = useMemo(() => {
    const bySo = new Map<number, { so_id: number; soName: string; lines: BillableLine[] }>();
    for (const l of lines) {
      const g = bySo.get(l.so_id);
      if (g) g.lines.push(l);
      else bySo.set(l.so_id, { so_id: l.so_id, soName: l.so_name, lines: [l] });
    }
    return [...bySo.values()];
  }, [lines]);
  const multiSo = groups.length > 1;

  if (lines.length === 0) return null;

  const renderLine = (l: BillableLine) => {
          const remaining = l.amount - l.billed;
          const disabled = !hasHeadroom(l);
          const checked = entries.has(l.id);
          const entry = entries.get(l.id) ?? emptyLineEntry;
          const previewRow = preview.find((p) => p.id === l.id);
          return (
            <li
              key={l.id}
              className={`px-3 py-2 text-sm ${disabled ? 'opacity-50' : ''} ${checked ? 'bg-td-blue/5' : ''}`}
            >
              <div className="flex items-start justify-between gap-2">
                <label className={`flex min-w-0 items-start gap-2 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => onToggleLine(l.id)}
                    className="mt-0.5 accent-td-blue"
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-td-navy" title={[l.name, l.detail, l.section].filter(Boolean).join(' — ')}>{l.name}</span>
                    {(l.detail || l.section) && (
                      <span className="block truncate text-[11px] text-td-muted">
                        {[l.detail, l.section].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </span>
                </label>
                <div className="shrink-0 text-right">
                  <p className="font-semibold text-td-navy">{fmtEur(l.amount)}</p>
                  {disabled ? (
                    <span className="inline-block rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-td-muted">
                      volledig gevorderd
                    </span>
                  ) : (
                    <p className="text-[11px] text-td-light">nog te vorderen: {fmtEur(remaining)}</p>
                  )}
                </div>
              </div>

              {/* Contract / gevorderd / gefactureerd for this line, mirroring the
                  per-SO table. Deliberately NOT summed: gevorderd (portal ledger) and
                  gefactureerd (Odoo) overlap, so they are shown side by side as two
                  views of the same line and the headroom above stays driven by their
                  max. Hidden when both are zero — an untouched line says nothing. */}
              {(l.gevorderd > 0 || l.gefactureerd > 0) && (
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 pl-6 text-[11px] text-td-muted">
                  <span>contract <span className="font-semibold text-td-navy">{fmtEur(l.amount)}</span></span>
                  <span>gevorderd <span className="font-semibold text-td-blue">{fmtEur(l.gevorderd)}</span></span>
                  <span>gefactureerd <span className="font-semibold text-td-green">{fmtEur(l.gefactureerd)}</span></span>
                </div>
              )}

              {/* Per-line inputs — only for a checked, non-disabled line. */}
              {checked && !disabled && (
                <div className="mt-2 space-y-1.5 pl-6">
                  {eindeWerken ? (
                    <p className="text-[11px] text-td-muted">
                      Einde werken: het volledige resterende bedrag van deze lijn wordt gevorderd.
                    </p>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      <div>
                        <label htmlFor={`vord-line-pct-${l.id}`} className="sr-only">Percentage</label>
                        <input
                          id={`vord-line-pct-${l.id}`}
                          type="number"
                          min={0}
                          max={100}
                          step="1"
                          inputMode="numeric"
                          // Shows the m²-derived suggestion as the field's VALUE while
                          // the entry is still empty, so a ticked line reads as the %
                          // it will actually bill instead of looking blank. Kept as a
                          // display default rather than seeded into state on toggle:
                          // the entry stays "empty = auto", so the number keeps
                          // following the collector selection until the PL types their
                          // own — seeding on tick would freeze the first value and
                          // silently bill the old % after collectors change.
                          value={entry.percentage !== '' ? entry.percentage : autoPct}
                          onChange={(e) => onEntryChange(l.id, { percentage: e.target.value })}
                          // Normalise to a whole percent on BLUR, not on every
                          // keystroke: rounding mid-typing would clear the field the
                          // moment a "." is entered. The preview below already shows
                          // the whole percent that will actually be billed, and both
                          // the submit payload and the API round as well.
                          onBlur={(e) => {
                            const raw = e.target.value.trim();
                            if (raw === '') return;
                            const n = Number(raw);
                            if (Number.isNaN(n)) return;
                            const whole = String(wholePercent(Math.min(100, Math.max(0, n))));
                            if (whole !== raw) onEntryChange(l.id, { percentage: whole });
                          }}
                          placeholder={`${wholePercent(autoPercentage)}%`}
                          className="w-20 rounded border border-gray-300 px-2 py-1 text-xs text-td-navy focus:border-td-blue focus:outline-none focus:ring-1 focus:ring-td-blue"
                        />
                      </div>
                      <input
                        type="text"
                        value={entry.description}
                        onChange={(e) => onEntryChange(l.id, { description: e.target.value })}
                        placeholder="Omschrijving (optioneel)"
                        className="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1 text-xs text-td-navy focus:border-td-blue focus:outline-none focus:ring-1 focus:ring-td-blue"
                      />
                    </div>
                  )}
                  {previewRow && (
                    <p className="truncate text-[11px] text-td-muted">
                      {previewRow.pct}% × {fmtEur(previewRow.lineAmount)} = <span className="font-semibold text-td-navy">{fmtEur(previewRow.amount)}</span>
                    </p>
                  )}
                  {previewRow?.capped && (
                    <p className="text-[11px] font-semibold text-amber-600">
                      Beperkt tot {previewRow.pct}%: je vroeg {previewRow.requestedPct}%,
                      maar er staat nog maar {fmtEur(previewRow.amount)} open op deze lijn.
                    </p>
                  )}
                  {showPercentageWarning && previewRow?.emptyPct && (
                    <p className="text-[11px] font-semibold text-td-magenta" role="alert">Vul een percentage in.</p>
                  )}
                </div>
              )}
            </li>
          );
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-1">
        <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">Te vorderen offertelijnen</p>
        {openCount >= 2 && (
          <span className="text-[11px] font-semibold text-td-blue">Kies welke offertelijn(en) je vordert.</span>
        )}
      </div>

      {m2Notice}

      {groups.map((g) => (
        <div key={g.so_id} className="space-y-1">
          {multiSo && (
            <p className="text-[11px] font-bold uppercase tracking-wide text-td-blue">{g.soName}</p>
          )}
          <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-100">
            {g.lines.map(renderLine)}
          </ul>
        </div>
      ))}

      {preview.length > 0 && (
        <p className="text-right text-sm font-bold text-td-navy">Totaal te vorderen: {fmtEur(total)}</p>
      )}

      {showRequiredError && (
        <p className="text-xs font-semibold text-td-magenta" role="alert">Selecteer minstens één offertelijn.</p>
      )}
    </div>
  );
}
